import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, errorMessage } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "../../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { LEAD_STAGES } from "../../lib/constants";
import { Plus, Search, Pencil, Trash2, ArrowUpDown, MapPin, Phone, Mail } from "lucide-react";
import { toast } from "sonner";

const STAGE_MAP = Object.fromEntries(LEAD_STAGES.map((s) => [s.key, s.label]));
const INVESTOR_TYPES = ["Flipper", "Buy & Hold", "Wholesaler", "Developer", "New Investor"];
const EXPERIENCE_LEVELS = ["Beginner", "Intermediate", "Advanced"];
const INTERESTED_IN = ["Fix & Flip", "DSCR Rental", "Bridge", "Construction", "Multifamily", "Cash-Out Refi", "Commercial", "Rental Portfolio"];

const EMPTY = {
  name: "", email: "", phone: "", city: "", state: "", investor_type: "",
  experience: "", interested_in: "", status: "new", priority: "normal", notes: "",
};

const stageBadge = (s) => {
  const map = {
    new: "bg-blue-50 text-blue-700 border-blue-200",
    contacted: "bg-cyan-50 text-cyan-700 border-cyan-200",
    interested: "bg-emerald-50 text-emerald-700 border-emerald-200",
    follow_up: "bg-amber-50 text-amber-700 border-amber-200",
    not_interested: "bg-slate-100 text-slate-600 border-slate-200",
    converted: "bg-violet-50 text-violet-700 border-violet-200",
  };
  return map[s] || "bg-slate-100 text-slate-600 border-slate-200";
};

export default function AdminLeads() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState({ key: "name", dir: "asc" });
  const [editing, setEditing] = useState(null); // lead obj or null
  const [form, setForm] = useState(EMPTY);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/admin/leads");
      setList(data);
    } catch (e) { toast.error(errorMessage(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let rows = list;
    if (q) {
      rows = rows.filter((l) =>
        [l.name, l.email, l.city, l.state, l.investor_type, l.interested_in, STAGE_MAP[l.status]]
          .filter(Boolean).join(" ").toLowerCase().includes(q)
      );
    }
    const dir = sort.dir === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
      const va = (a[sort.key] || "").toString().toLowerCase();
      const vb = (b[sort.key] || "").toString().toLowerCase();
      return va < vb ? -dir : va > vb ? dir : 0;
    });
  }, [list, query, sort]);

  const toggleSort = (key) =>
    setSort((s) => ({ key, dir: s.key === key && s.dir === "asc" ? "desc" : "asc" }));

  const openAdd = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (l) => {
    setEditing(l);
    setForm({ ...EMPTY, ...l });
    setOpen(true);
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name, email: form.email, phone: form.phone, city: form.city,
        state: form.state, investor_type: form.investor_type, experience: form.experience,
        interested_in: form.interested_in, status: form.status, priority: form.priority, notes: form.notes,
      };
      if (editing) {
        await api.patch(`/admin/leads/${editing.lead_id}`, payload);
        toast.success("Lead updated");
      } else {
        await api.post("/admin/leads", payload);
        toast.success("Lead added");
      }
      setOpen(false);
      load();
    } catch (e) { toast.error(errorMessage(e)); }
    finally { setSaving(false); }
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/admin/leads/${deleteTarget.lead_id}`);
      toast.success("Lead deleted");
      setDeleteTarget(null);
      setOpen(false);
      load();
    } catch (e) { toast.error(errorMessage(e)); }
  };

  const SortHead = ({ k, children, className = "" }) => (
    <button
      onClick={() => toggleSort(k)}
      className={`flex items-center gap-1 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 hover:text-blue-600 ${className}`}
      data-testid={`lead-sort-${k}`}
    >
      {children}
      <ArrowUpDown className="h-3 w-3 opacity-60" />
    </button>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900">Leads</h1>
          <p className="mt-1 text-sm text-slate-500">Manage your pipeline — add, edit, sort and track leads.</p>
        </div>
        <Button onClick={openAdd} className="bg-blue-600 text-white hover:bg-blue-700 self-start sm:self-auto" data-testid="add-lead-btn">
          <Plus className="mr-2 h-4 w-4" /> Add Lead
        </Button>
      </div>

      <div className="relative max-w-md">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search name, location, type…"
          className="pl-9"
          data-testid="lead-search-input"
        />
      </div>

      {/* Desktop table */}
      <Card className="hidden overflow-hidden md:block">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-200">
                <th className="px-4 py-3"><SortHead k="name">Name</SortHead></th>
                <th className="px-4 py-3"><SortHead k="city">Location</SortHead></th>
                <th className="px-4 py-3"><SortHead k="investor_type">Investor</SortHead></th>
                <th className="px-4 py-3"><SortHead k="interested_in">Interested</SortHead></th>
                <th className="px-4 py-3"><SortHead k="status">Stage</SortHead></th>
                <th className="px-4 py-3"><SortHead k="priority">Priority</SortHead></th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Actions</th>
              </tr>
            </thead>
            <tbody>
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-slate-500" data-testid="leads-empty">No leads yet. Click "Add Lead" to create one.</td></tr>
              )}
              {filtered.map((l) => (
                <tr
                  key={l.lead_id}
                  onClick={() => openEdit(l)}
                  className="cursor-pointer border-b border-slate-100 transition-colors hover:bg-blue-50/50"
                  data-testid={`admin-lead-${l.lead_id}`}
                >
                  <td className="px-4 py-3">
                    <div className="font-semibold text-slate-900">{l.name}</div>
                    {l.email && <div className="text-xs text-slate-500">{l.email}</div>}
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600">{[l.city, l.state].filter(Boolean).join(", ") || "—"}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{l.investor_type || "—"}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{l.interested_in || "—"}</td>
                  <td className="px-4 py-3"><Badge variant="outline" className={stageBadge(l.status)}>{STAGE_MAP[l.status] || l.status}</Badge></td>
                  <td className="px-4 py-3">
                    {l.priority === "high"
                      ? <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">High</Badge>
                      : <span className="text-xs text-slate-400">Normal</span>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-1" onClick={(e) => e.stopPropagation()}>
                      <Button size="icon" variant="ghost" onClick={() => openEdit(l)} className="h-8 w-8 text-slate-500 hover:text-blue-600" data-testid={`lead-edit-${l.lead_id}`}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => setDeleteTarget(l)} className="h-8 w-8 text-slate-500 hover:text-rose-600" data-testid={`lead-delete-${l.lead_id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Mobile cards */}
      <div className="space-y-3 md:hidden">
        {!loading && filtered.length === 0 && (
          <Card className="p-6 text-center text-sm text-slate-500">No leads yet. Tap "Add Lead" to create one.</Card>
        )}
        {filtered.map((l) => (
          <Card key={l.lead_id} onClick={() => openEdit(l)} className="cursor-pointer p-4" data-testid={`admin-lead-m-${l.lead_id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="truncate font-semibold text-slate-900">{l.name}</div>
                {l.email && <div className="truncate text-xs text-slate-500"><Mail className="mr-1 inline h-3 w-3" />{l.email}</div>}
                {l.phone && <div className="truncate text-xs text-slate-500"><Phone className="mr-1 inline h-3 w-3" />{l.phone}</div>}
              </div>
              {l.priority === "high" && <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 shrink-0">High</Badge>}
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-slate-600">
              {(l.city || l.state) && <span className="inline-flex items-center"><MapPin className="mr-1 h-3 w-3" />{[l.city, l.state].filter(Boolean).join(", ")}</span>}
              {l.investor_type && <span className="rounded-sm bg-slate-100 px-2 py-0.5">{l.investor_type}</span>}
              {l.interested_in && <span className="rounded-sm bg-slate-100 px-2 py-0.5">{l.interested_in}</span>}
              <Badge variant="outline" className={stageBadge(l.status)}>{STAGE_MAP[l.status] || l.status}</Badge>
            </div>
          </Card>
        ))}
      </div>

      {/* Add / Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="lead-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Lead" : "Add Lead"}</DialogTitle>
            <DialogDescription>{editing ? "Update this lead's details." : "Create a new lead in your pipeline."}</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <Label>Name *</Label>
              <Input value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1" data-testid="lead-form-name" />
            </div>
            <div>
              <Label>Email</Label>
              <Input value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-1" data-testid="lead-form-email" />
            </div>
            <div>
              <Label>Phone</Label>
              <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="mt-1" data-testid="lead-form-phone" />
            </div>
            <div>
              <Label>City</Label>
              <Input value={form.city} onChange={(e) => set("city", e.target.value)} className="mt-1" data-testid="lead-form-city" />
            </div>
            <div>
              <Label>State</Label>
              <Input value={form.state} onChange={(e) => set("state", e.target.value)} className="mt-1" data-testid="lead-form-state" />
            </div>
            <div>
              <Label>Investor Type</Label>
              <Select value={form.investor_type} onValueChange={(v) => set("investor_type", v)}>
                <SelectTrigger className="mt-1" data-testid="lead-form-investor"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{INVESTOR_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Experience</Label>
              <Select value={form.experience} onValueChange={(v) => set("experience", v)}>
                <SelectTrigger className="mt-1" data-testid="lead-form-experience"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{EXPERIENCE_LEVELS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Interested In</Label>
              <Select value={form.interested_in} onValueChange={(v) => set("interested_in", v)}>
                <SelectTrigger className="mt-1" data-testid="lead-form-interested"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{INTERESTED_IN.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Stage</Label>
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger className="mt-1" data-testid="lead-form-stage"><SelectValue /></SelectTrigger>
                <SelectContent>{LEAD_STAGES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Priority</Label>
              <Select value={form.priority} onValueChange={(v) => set("priority", v)}>
                <SelectTrigger className="mt-1" data-testid="lead-form-priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <Label>Notes</Label>
              <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} className="mt-1 min-h-[80px]" data-testid="lead-form-notes" />
            </div>
          </div>
          <DialogFooter className="flex-col-reverse gap-2 sm:flex-row sm:justify-between">
            {editing ? (
              <Button variant="ghost" onClick={() => setDeleteTarget(editing)} className="text-rose-600 hover:bg-rose-50 hover:text-rose-700" data-testid="lead-dialog-delete">
                <Trash2 className="mr-2 h-4 w-4" /> Delete
              </Button>
            ) : <span />}
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setOpen(false)} data-testid="lead-dialog-cancel">Cancel</Button>
              <Button onClick={save} disabled={saving} className="bg-blue-600 text-white hover:bg-blue-700" data-testid="lead-dialog-save">
                {saving ? "Saving…" : editing ? "Save Changes" : "Add Lead"}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="lead-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this lead?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.name} will be permanently removed. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="lead-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-rose-600 text-white hover:bg-rose-700" data-testid="lead-delete-confirm-btn">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
